<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  LOCKED_THEME,
  SpBadge,
  SpIcon,
  SpLogo,
  SpToast,
  useTheme,
  type IconName,
} from '@servepod/ui';
import { useAdminSession } from './stores/session';
import MfaChallengeModal from './components/MfaChallengeModal.vue';
import { setMfaRequiredHandler } from './lib/api';

const route = useRoute();
const router = useRouter();
const session = useAdminSession();
const { resolved, toggle } = useTheme();

const mobileOpen = ref(false);
const mfaOpen = ref(false);
setMfaRequiredHandler(() => {
  mfaOpen.value = true;
});
const chrome = computed(() => route.meta.chrome !== false);

/** Grouped by what an operator is looking after: hardware, customers, the platform's rules. */
const NAV: { heading: string | null; items: { to: string; label: string; icon: IconName }[] }[] = [
  { heading: null, items: [{ to: '/', label: 'Overview', icon: 'dashboard' }] },
  {
    heading: 'Inventory',
    items: [
      { to: '/fleet', label: 'Fleet', icon: 'server' },
      { to: '/accelerators', label: 'Accelerators', icon: 'gpu' },
    ],
  },
  {
    heading: 'Business',
    items: [
      { to: '/accounts', label: 'Accounts', icon: 'users' },
      { to: '/pods', label: 'Pods', icon: 'pods' },
      { to: '/revenue', label: 'Revenue', icon: 'billing' },
    ],
  },
  {
    heading: 'Administration',
    items: [
      { to: '/billing-settings', label: 'Enforcement', icon: 'shield' },
      { to: '/audit', label: 'Audit', icon: 'audit' },
      { to: '/security', label: 'Security', icon: 'shield' },
    ],
  },
];

const active = computed(
  () => (to: string) => (to === '/' ? route.path === '/' : route.path.startsWith(to)),
);

async function signOut() {
  await session.signOut();
  void router.push({ name: 'sign-in' });
}

onMounted(() => {
  if (session.status === 'unknown') void session.refresh();
});
</script>

<template>
  <div v-if="chrome" class="min-h-screen bg-canvas">
    <!-- A permanent, unmissable marker that this is the operator console. Actions here
         affect other people's running workloads, and the interface should never let an
         operator forget which portal they are in. -->
    <div
      class="flex h-8 items-center justify-center gap-2 border-b border-accent-border bg-accent-subtle text-2xs font-medium uppercase tracking-[0.14em] text-accent"
    >
      <SpIcon name="shield" :size="12" />
      ServePod operator console
    </div>

    <div class="flex">
      <aside
        class="fixed inset-y-0 left-0 z-40 flex w-56 flex-col border-r border-line bg-surface transition-transform duration-fast lg:translate-x-0"
        :class="mobileOpen ? 'translate-x-0' : '-translate-x-full'"
      >
        <div class="flex h-14 shrink-0 items-center gap-2.5 border-b border-line px-4">
          <SpLogo :height="26" />
          <span class="text-2xs font-medium uppercase tracking-[0.14em] text-ink-muted">
            Admin
          </span>
        </div>

        <!-- Who is signed in, directly under the logo: every action here is audited
             against this identity, so it should never be out of view. -->
        <div class="border-b border-line p-3">
          <div class="rounded-lg border border-line bg-surface-raised p-3">
            <p class="truncate text-xs font-medium">{{ session.me?.user.email }}</p>
            <div class="mt-2 flex flex-wrap gap-1">
              <SpBadge v-for="role in session.roles" :key="role" tone="accent">
                {{ role.replace('platform.', '') }}
              </SpBadge>
            </div>
          </div>
        </div>

        <nav class="flex-1 overflow-y-auto p-3" aria-label="Main">
          <div v-for="group in NAV" :key="group.heading ?? 'top'" :class="group.heading && 'mt-5'">
            <p
              v-if="group.heading"
              class="mb-1.5 px-2.5 text-2xs font-medium uppercase tracking-wider text-ink-muted"
            >
              {{ group.heading }}
            </p>
            <div class="space-y-0.5">
              <RouterLink
                v-for="item in group.items"
                :key="item.to"
                :to="item.to"
                class="flex items-center gap-2.5 rounded px-2.5 py-2 text-sm font-medium transition-colors duration-fast"
                :class="
                  active(item.to)
                    ? 'bg-accent-subtle text-ink'
                    : 'text-ink-secondary hover:bg-surface-hover hover:text-ink'
                "
                :aria-current="active(item.to) ? 'page' : undefined"
                @click="mobileOpen = false"
              >
                <SpIcon :name="item.icon" :size="17" />
                {{ item.label }}
              </RouterLink>
            </div>
          </div>
        </nav>

        <div class="border-t border-line p-3">
          <button
            type="button"
            class="flex w-full items-center gap-2.5 rounded px-2.5 py-2 text-sm font-medium text-ink-secondary transition-colors duration-fast hover:bg-danger-subtle hover:text-danger"
            @click="signOut"
          >
            <SpIcon name="logout" :size="17" />
            Sign out
          </button>
        </div>
      </aside>

      <div
        v-if="mobileOpen"
        class="fixed inset-0 z-30 bg-black/50 lg:hidden"
        aria-hidden="true"
        @click="mobileOpen = false"
      />

      <div class="flex min-h-screen w-full flex-col lg:pl-56">
        <header
          class="sticky top-8 z-20 flex h-14 shrink-0 items-center gap-3 border-b border-line bg-canvas/85 px-4 backdrop-blur lg:px-6"
        >
          <button
            type="button"
            class="grid h-9 w-9 place-items-center rounded text-ink-secondary transition-colors hover:bg-surface-hover lg:hidden"
            aria-label="Open navigation"
            @click="mobileOpen = true"
          >
            <SpIcon name="menu" :size="18" />
          </button>
          <div class="flex-1" />
          <button
            v-if="LOCKED_THEME === null"
            type="button"
            class="grid h-9 w-9 place-items-center rounded text-ink-secondary transition-colors hover:bg-surface-hover hover:text-ink"
            :aria-label="resolved === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'"
            @click="toggle"
          >
            <SpIcon :name="resolved === 'dark' ? 'sun' : 'moon'" :size="17" />
          </button>
        </header>

        <main class="flex-1 px-4 py-6 lg:px-8">
          <div class="mx-auto w-full max-w-[1600px]">
            <RouterView />
          </div>
        </main>
      </div>
    </div>
  </div>

  <RouterView v-else />
  <MfaChallengeModal :open="mfaOpen" @close="mfaOpen = false" />
  <SpToast />
</template>
